import { useState, useEffect } from 'react';
import { 
  connectWallet, 
  listenToAccountChanges, 
  listenToChainChanges,
  switchToCorrectNetwork,
  getBalance,
  getUSDTBalance
} from '../utils/web3';
import { CURRENT_NETWORK } from '../config/network';

export const useWallet = () => {
  const [account, setAccount] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [balance, setBalance] = useState('0');
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [chainId, setChainId] = useState(null);
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(true);

  useEffect(() => {
    checkConnection();
    setupListeners();
  }, []);

  useEffect(() => {
    if (account) updateBalances();
  }, [account]);

  useEffect(() => {
    if (chainId) {
      setIsCorrectNetwork(chainId === CURRENT_NETWORK.chainId);
      if (chainId !== CURRENT_NETWORK.chainId) {
        handleNetworkSwitch();
      }
    }
  }, [chainId]);

  const checkConnection = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
          setChainId(currentChainId);
        }
      } catch (error) {
        console.error('Error checking connection:', error);
      }
    }
  };

  const setupListeners = () => {
    listenToAccountChanges((newAccount) => {
      setAccount(newAccount);
      if (!newAccount) {
        setBalance('0');
        setUsdtBalance('0');
      }
    });
    listenToChainChanges(setChainId);
  };

  const handleNetworkSwitch = async () => {
    try {
      await switchToCorrectNetwork();
    } catch (error) {
      console.error('Error switching network:', error);
    }
  };

  const updateBalances = async () => {
    try {
      const [bnbBal, usdtBal] = await Promise.all([
        getBalance(account),
        getUSDTBalance(account)
      ]);
      setBalance(bnbBal);
      setUsdtBalance(usdtBal);
    } catch (error) {
      console.error('Error updating balances:', error);
    }
  };

  const connect = async () => {
    setIsConnecting(true);
    try {
      const connectedAccount = await connectWallet();
      setAccount(connectedAccount);
      const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
      setChainId(currentChainId);
      return connectedAccount;
    } catch (error) {
      console.error('Connection error:', error);
      throw error;
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = () => {
    setAccount(null);
    setBalance('0');
    setUsdtBalance('0');
  };

  const refreshBalances = () => {
    if (account) updateBalances();
  };

  return {
    account,
    balance,
    usdtBalance,
    isConnecting,
    isConnected: !!account,
    isCorrectNetwork,
    connect,
    disconnect,
    refreshBalances
  };
};
